import { Inject, Options, Ref, Vue } from "vue-property-decorator";
import LoadingSpinner from "@/shared/components/loading-spinner/index.vue";
import { AuthorizedUser } from '@/trust-atcs-app/models/authorized-user';
import store from '../../store';
import { TrustAPI } from "@/shared/modules/trust-api";
import PostcodeSearch from "@/shared/components/postcodeSearch/index.vue";
import FileDragAndDrop from "@/shared/components/file-drag-and-drop/index.vue";
import EEDashboard from "../ee-dashboard/index.vue";
import { RouteN } from "@/trust-atcs-app/constants/router-name";
import { EeSignUpFormRequest, SuburbStatePostcode } from "@/shared/modules/typescript-trustapi-client/models";
import ConfirmDialogue from '@/shared/components/confirm-dialogue/index.vue';
import { EventEmitter } from "events";
import LoadingIndicator from '@/shared/components/loading-indicator/index.vue';
import { AppInsights } from "@/trust-atcs-app/appInsights";

@Options({
  components: {
    LoadingSpinner,
    PostcodeSearch,
    EEDashboard,
    FileDragAndDrop,
    ConfirmDialogue,
    LoadingIndicator
  }
})
export default class EESignup extends Vue {

  @Ref('fileDropAndDrop') readonly fileDropAndDrop!: any;

  @Inject({ from: 'apiService', default: new TrustAPI() })
  private apiService!: TrustAPI;

  private ai = new AppInsights;

  private waitingApproval = false;
  private iWantToSignUp = false;
  private loadingDataSet = false;

  private membershipStatusTypes: any = [];

  private selectedMembershipType: any = null;
  private suburbStatePostcode: SuburbStatePostcode = {};
  private fileList: Array<File> = [];
  private uploadedFileList: Array<any> = [];
  private uploading = false;
  private submitting = false;
  private submitPressed = false;

  private firmName = null;
  private address = null;
  private phoneNumber = null;
  private publishOnWeb = true; // default to true

  private dirtyFields: Array<boolean> = [];

  created(): void {
    this.preloadData();
    this.dirtyFields = [false, false, false, false, false, false];
  }

  private async preloadData(): Promise<void> {
    try {
      this.loadingDataSet = true;
      const resp: any = await Promise.all([
        this.apiService.CodeApi?.apiV2CodeGetEeMembershipTypesGet(),
        this.apiService.CodeApi?.apiV2CodeGetEeSignUpFormStatusesGet(),
        this.apiService.ExternalExaminerApi?.apiV2ExternalExaminerGetEeSignUpFormGet(),
      ]);

      this.membershipStatusTypes = resp[0].data.data;
      this.waitingApproval = resp[2].data.data ? true : false;

    } catch (err) {
      console.log(err);
      this.ai.trackException('Error, Unable to load current status: ' + err);
      this.eventCtrl.emit('show-snackbar', 'error', 'Unable to load current status: ' + err);
    } finally {
      this.loadingDataSet = false;
    }
  }

  private async signUpForEE(): Promise<void> {
    try {
      this.submitPressed = true;
      if (!this.isFormValid()) {
        throw ('Incomplete form');
      }
      this.submitting = true;
      const body: EeSignUpFormRequest = {
        id: 0,
        firmName: this.firmName,
        address: this.address,
        suburb: this.suburbStatePostcode.suburbName,
        postCode: this.suburbStatePostcode.postcode,
        state: this.suburbStatePostcode.stateCode,
        phoneDetail: {
          countryCode: '+61',
          phoneNumber: this.phoneNumber
        },
        eeMembershipType: this.selectedMembershipType,
        publishOnWeb: this.publishOnWeb,
        files: this.uploadedFileList.map(x => { return { id: x.id }; })

      };
      const resp: any = await this.apiService.ExternalExaminerApi?.apiV2ExternalExaminerSaveEeSignUpFormPost(body);
      this.eventCtrl.emit('show-snackbar', 'success', 'Application has been submitted');
      this.waitingApproval = true;
      this.iWantToSignUp = false;

    } catch (err) {
      this.eventCtrl.emit('show-snackbar', 'error', 'Error signing up: ' + err);
    } finally {
      this.submitting = false;
    }
  }

  private fileListChanged(fileList: Array<File>): void {
    this.fileList = fileList;
    this.uploadAllFiles(this.fileList);
  }

  private deleteFile(id: number): void {
    const _confirm: any = this.$refs.confirmDialogue;
    _confirm.show({
      title: 'Delete File',
      message: 'Are you sure you want to delete this file?',
      okButton: 'Delete',
      cancelButton: 'Cancel',
    }).then((res: any) => {
      if (res) {
        this.deleteFileFromServer(id);
      }
    })

  }

  private async deleteFileFromServer(id: number): Promise<void> {
    try {
      await this.apiService.FileAtcsApi?.apiV2FileAtcsDeleteFileFileIdDelete(id);

      this.uploadedFileList = this.uploadedFileList.filter(x => x.id !== id);
      this.eventCtrl.emit('show-snackbar', 'success', 'File removed.');

    } catch (err) {
      // this.ai.trackException('Error on delete file: ' + JSON.stringify(err));
      this.eventCtrl.emit('show-snackbar', 'error', 'Error on delete file: ' + err);
    }
  }

  private async uploadAllFiles(fileList: Array<File>): Promise<void> {
    const allRequests: Array<Promise<boolean>> = [];
    this.uploading = true;
    try {
      if (fileList.length > 0) {
        fileList.forEach((x) => allRequests.push(this.uploadFile(x)));
        await Promise.all(allRequests);
        this.eventCtrl.emit('show-snackbar', 'success', 'File(s) uploaded');
        this.fileList = [];
        this.fileDropAndDrop.selectedFiles = [];
      }
    } catch (err: any) {
      // this.ai.trackException('Error on upload file: ' + JSON.stringify(err));
      this.eventCtrl.emit('show-snackbar', 'error', 'Error on upload file: ' + err);
    } finally {
      this.uploading = false;
    }
  }

  private async uploadFile(file: File): Promise<boolean> {
    try {
      const fileStr = await this.fileToString(file);
      const body = {
        fileName: file.name,
        fileExtension: file.name,
        fileDataUrl: fileStr,
        id: ''
      };

      const res: any = await this.apiService.FileAtcsApi?.apiV2FileAtcsSaveFilePost(body);
      body.id = res.data.data;
      body.fileDataUrl = '';
      this.uploadedFileList.push(body);

      return Promise.resolve(true);
    } catch (err) {
      console.log(err);
      this.ai.trackException('Error on upload file: ' + err);
      this.eventCtrl.emit('show-snackbar', 'error', 'Error on upload file: ' + err);
      return Promise.reject(false);
    }
  }

  private fileToString(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        return resolve(reader.result as string);
      };

      reader.onabort = () => {
        return reject();
      }

      reader.onerror = () => {
        return reject();
      }
    });
  }

  private updateSuburb(s: SuburbStatePostcode) {
    this.suburbStatePostcode = s;
    this.dirtyFields[2] = true;
  }

  private signOut() {
    this.$router.push({ name: RouteN.ATCS.LogoutPage.n });
  }

  private isFormValid(): boolean {
    return !!this.firmName && !!this.address && !!this.phoneNumber &&
      !!this.suburbStatePostcode?.postcode && this.selectedMembershipType != null &&
      this.uploadedFileList.length > 0;
  }

  get eeStatusType(): number {
    let eeType = -1;
    if (this.userAccount.user.lawIdUserTypeId == 2) {
      eeType = 0;
    } else if (this.userAccount.user.lawIdUserTypeId == 3) {
      eeType = 1; // Sign up required
    }
    return eeType;
  }

  get suburbError(): boolean {
    return (!this.suburbStatePostcode?.postcode && (this.dirtyFields[2] || this.submitPressed));
  }

  get eventCtrl(): EventEmitter {
    return store.getters.getEventController;
  }

  get userAccount(): AuthorizedUser {
    return store.getters.getUserAccount;
  }
}

